import Promise from '../promise';
import { Store, StoreAction, StoreEvent } from './baseStore';
import { getSettings, setSettings } from '../services/settingsService';
import * as constants from '../constants/core';
import * as config from '../dataServices/servicesConfig';
import * as tokenService from '../dataServices/token';
import * as i18nService from '../services/i18nService';
import { CoreSettings } from '../typings/CoreSettings';
import { Token } from '../typings/Token';
import { User } from '../typings/User';
import { Logger } from '../typings/Logger';
import { clearOutdatedItems, clearOutdatedNamespaceItems } from '../services/storageService';
import { isNumber } from '../services/util';

export interface CoreState {
  logger: Logger;
  settings: CoreSettings;
  user: User.User;
}

export interface CoreAction extends StoreAction {
  language?: string;
  logger?: Logger;
  settings?: CoreSettings;
  tokenObject?: Token.Token;
  userData?: User.User;
}

const updateEnvironment = (environment: string): void => {
  if (!config.isSet() || config.getConfig().ENVIRONMENT !== environment) {
    config.setEnvironment(environment);
  }
};

const updateClientId = (clientId: string): void => {
  if (config.isSet() && clientId !== config.getConfig().CLIENT_ID) {
    config.setClientId(clientId);
  }
};

const updateSalesClient = (salesClient: string): void => {
  if (config.isSet() && salesClient !== config.getConfig().SALES_CLIENT) {
    config.setSalesClient(salesClient);
  }
};

const applySettings = (newSettings: CoreSettings): void => {
  setSettings(newSettings);

  if (isNumber(newSettings.storageVersion)) {
    clearOutdatedItems(newSettings.storageVersion);
  }

  if (newSettings.storageNamespace && isNumber(newSettings.storageNamespaceVersion)) {
    clearOutdatedNamespaceItems(newSettings.storageNamespace, newSettings.storageNamespaceVersion);
  }

  if (newSettings.environment) {
    updateEnvironment(newSettings.environment);
  }

  if (newSettings.clientId) {
    updateClientId(newSettings.clientId);
  }

  if (newSettings.tokenObject) {
    updateToken(newSettings.tokenObject);
  }

  if (newSettings.language) {
    i18nService.setLanguage(newSettings.language);
  }

  if (newSettings.salesClient) {
    updateSalesClient(newSettings.salesClient);
  }
};

const updateSettings = (state: CoreState, newSettings: CoreSettings): CoreState => {
  applySettings(newSettings);

  return {
    ...state,
    settings: getSettings()
  };
};

/**
 * Sets new authentication token
 * @param {ApiToken} newToken
 */
const updateToken = (newToken: Token.ApiToken): void => {
  tokenService.setToken(newToken);
};

/**
 * Removes current authentication token
 */
const removeToken = (): void => {
  tokenService.remove();
  tokenService.removeGeoToken();
};

const logoutUser = (state: CoreState): CoreState => ({
  ...state,
  user: null
});

const setUser = (state: CoreState, userData: User.User): CoreState => ({
  ...state,
  user: userData
});

const setLogger = (state: CoreState, logger: Logger): CoreState => ({
  ...state,
  logger
});

export const eventCollector = (_: CoreState, action: CoreAction): StoreEvent | StoreEvent[] => {
  switch (action.actionType) {
    case constants.CHANGE_LANGUAGE_ACTION:
      return { name: constants.LANGUAGE_CHANGED_EVENT };
    case constants.CHANGE_SETTINGS_ACTION: {
      const changeSettingsEvents = [{ name: constants.SETTINGS_CHANGED_EVENT }];

      if (action.settings && action.settings.language) {
        changeSettingsEvents.push({ name: constants.LANGUAGE_CHANGED_EVENT });
      }

      return changeSettingsEvents;
    }

    case constants.AVAILABILITY_CHECKED_ACTION:
      return { name: constants.AVAILABILITY_CHECKED_EVENT };
    case constants.UPDATE_TOKEN_ACTION:
    case constants.REMOVE_TOKEN_ACTION:
    case constants.TOKEN_CHANGE_ACTION:
      return { name: constants.TOKEN_CHANGE_EVENT };
    case constants.LOGOUT_ACTION:
      return [{ name: constants.USER_CHANGE_EVENT }, { name: constants.LOGOUT_EVENT }];
    case constants.UPDATE_USER_ACTION:
      return { name: constants.USER_CHANGE_EVENT };
    case constants.LOGIN_SUCCESS_ACTION:
      return { name: constants.LOGIN_SUCCESS_EVENT };
    case constants.LOGIN_FAILED_ACTION:
      return { name: constants.LOGIN_FAILED_EVENT };
    case constants.TOKEN_NOT_FOUND_ACTION:
      return { name: constants.TOKEN_NOT_FOUND_EVENT };
    case constants.LOGIN_SERVICE_UNAVAILABLE_ACTION:
      return { name: constants.LOGIN_SERVICE_UNAVAILABLE_EVENT };
    default:
      return [];
  }
};

export const stateReducer = (state: CoreState, action: CoreAction): CoreState => {
  switch (action.actionType) {
    case constants.CHANGE_LANGUAGE_ACTION:
      return updateSettings(state, { language: action.language });

    case constants.CHANGE_SETTINGS_ACTION:
      return updateSettings(state, action.settings);

    case constants.UPDATE_TOKEN_ACTION:
      updateToken(action.tokenObject);

      return state;

    case constants.REMOVE_TOKEN_ACTION:
      removeToken();

      return state;

    case constants.LOGOUT_ACTION:
      return logoutUser(state);

    case constants.UPDATE_USER_ACTION:
      return setUser(state, action.userData);

    case constants.SET_LOGGER_ACTION:
      return setLogger(state, action.logger);

    default:
      return state;
  }
};

export class CoreStore extends Store<CoreState> {
  /**
   * @description Check if user can change own password
   * @returns {boolean} True if user can change own password
   */
  canChangePassword(): boolean {
    return this.isAuthenticated() && !this.isAnonymousCustomer() && this.getUser().canChangePassword;
  }

  getAccessToken(): Promise<string> | string {
    return tokenService.getAccessToken();
  }

  /**
   * @description Get the users client id
   * @returns {Promise<string> | string}  Client id of the current user
   */
  getClientId(): Promise<string> | string {
    return tokenService.getClientId();
  }

  getGeoToken(): Promise<Token.GeoToken> {
    return tokenService.getGeoToken();
  }

  /**
   * @description Get logger
   * @returns {Logger} Logger
   */
  getLogger(): Logger {
    return this.getState().logger;
  }

  /**
   * @description Get user logon ident
   * @returns {string} Logon ident of current user
   */
  getLogonIdent(): string {
    const user = this.getUser();

    return user ? user.logonIdent : '';
  }

  /**
   * Returns current application settings
   * @returns {CoreSettings} Settings object
   */
  getSettings(): CoreSettings {
    return this.getState().settings;
  }

  /**
   * @description Get user data for currently logged in user
   * @returns {User} Currently logged in UserData
   */
  getUser(): User.User {
    return this.getState().user;
  }

  /**
   * @description Get user email
   * @returns {string} Email of current user
   */
  getUserEmail(): string {
    const user = this.getUser();

    return user ? user.email : '';
  }

  /**
   * @description Get user name
   * @returns {string} Name of current user
   */
  getUserName(): string {
    const user = this.getUser();

    return user ? user.name : '';
  }

  /**
   * @description Get user organization code
   * @returns {string} Organization code of current user
   */
  getUserOrganizationCode(): string {
    const user = this.getUser();
    const code = user && user.organization && user.organization.code;

    return code || '';
  }

  /**
   * @description Check if user has access only to Norges Eiendommer
   * @returns {boolean} True if user has access only to Norges Eiendommer
   */
  hasAccessOnlyToNorgesEiendommer(): boolean {
    return !this.isUserAbleToShop() && this.hasAccessToNorgesEiendommer();
  }

  /**
   * @description Check if user has access to Norges Eiendommer Basis
   * @returns {boolean} True if user has access to Norges Eiendommer Basis
   */
  hasAccessToNewBasis(): boolean {
    return this.isAuthenticated() && this.getUser().isNewBasis;
  }

  /**
   * @description Check if user has access to Norges Eiendommer Eksport
   * @returns {boolean} True if user has access to Norges Eiendommer Eksport
   */
  hasAccessToNewEksport(): boolean {
    return this.isAuthenticated() && this.getUser().isNewEksport;
  }

  /**
   * @description Check if user has access to Norges Eiendommer
   * @returns {boolean} True if user has access to Norges Eiendommer
   */
  hasAccessToNorgesEiendommer(): boolean {
    return this.hasAccessToNewBasis() || this.hasAccessToNewEksport();
  }

  /**
   * @description Check if user has any admin rights
   * @returns {boolean} True if user has any admin rights
   */
  hasAdminRights(): boolean {
    return this.isUserAdmin() || this.isCustomerAdmin() || this.isSuperAdmin();
  }

  /**
   * @description Check if user is an anonymous customer
   * @returns {boolean} True if user is an anonymous customer
   */
  isAnonymousCustomer(): boolean {
    return this.isAuthenticated() && this.getUser().isAnonymousCustomer;
  }

  /**
   * @description Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getUser();
  }

  /**
   * @description Check if user is authenticated and is not an anonymous customer
   * @returns {boolean} True if user is authenticated and is not an anonymous customer
   */
  isAuthenticatedNotAnonymousCustomer(): boolean {
    return this.isAuthenticated() && !this.getUser().isAnonymousCustomer;
  }

  /**
   * @description Check if user is a credit card customer
   * @returns {boolean} True if user is a credit card customer
   */
  isCreditCardCustomer(): boolean {
    return this.isAuthenticated() && this.getUser().isCreditCardCustomer;
  }

  /**
   * @description Check if user is a customer admin
   * @returns {boolean} True if user is a customer admin
   */
  isCustomerAdmin(): boolean {
    return this.isAuthenticated() && this.getUser().isCustomerAdmin;
  }

  /**
   * @description Check if the user an integrator
   * @returns {boolean} True if user is an integrator
   */
  isIntegratorUser(): boolean {
    return this.isAuthenticated() && this.getUser().organization.isIntegrator;
  }

  /**
   * @description Check if user is new Infoland exclusive
   * @returns {boolean} True if user is new Infoland exclusive
   */
  isNewInfolandExclusive(): boolean {
    return this.isAuthenticated() && this.getUser().isNewInfolandExclusive;
  }

  /**
   * @description Check if components is in serviceTorg-mode
   * @returns {boolean} True if serviceTorg is set
   */
  isServiceTorg(): boolean {
    return !!this.getState().settings.serviceTorg;
  }

  /**
   * @description Check if user is authenticated and is not an anonymous customer and is not a supplier
   * @returns {boolean} True if user is authenticated and is not an anonymous customer and is not a supplier
   */
  isStandardCustomer(): boolean {
    return this.isAuthenticated() && !this.isAnonymousCustomer() && !this.isSupplier();
  }

  /**
   * @description Check if user is a subscription customer
   * @returns {boolean} True if user is a subscription customer
   */
  isSubscriptionCustomer(): boolean {
    return this.isAuthenticated() && this.getUser().isSubscriptionCustomer;
  }

  /**
   * @description Check if user is a subscription customer or a supplier
   * @returns {boolean} True if user is a subscription customer or a supplier
   */
  isSubscriptionCustomerOrSupplier(): boolean {
    return this.isSubscriptionCustomer() || this.isSupplier();
  }

  /**
   * @description Check if user is a super admin
   * @returns {boolean} True if user is a super admin
   */
  isSuperAdmin(): boolean {
    return this.isAuthenticated() && this.getUser().isSuperAdmin;
  }

  /**
   * @description Check if user is a supplier
   * @returns {boolean} True if user is a supplier
   */
  isSupplier(): boolean {
    return this.isAuthenticated() && this.getUser().isSupplier;
  }

  /**
   * @description Check if user is able to shop
   * @returns {boolean} True if user is able to shop
   */
  isUserAbleToShop(): boolean {
    return this.isAuthenticated() && this.getUser().isUserAbleToShop;
  }

  /**
   * @description Check if user is an admin
   * @returns {boolean} True if user is an admin
   */
  isUserAdmin(): boolean {
    return this.isAuthenticated() && this.getUser().isUserAdmin;
  }

  /**
   * @description Check if username is authenticated
   * @returns {boolean} True if username is authenticated
   */
  isUsernameAuthenticated(username: string): boolean {
    if (!this.isAuthenticated() || !username) {
      return false;
    }

    return this.getLogonIdent().toLowerCase() === username.toLowerCase();
  }

  /**
   * @description Check if username is authenticated as a credit card customer
   * @returns {boolean} True if username is authenticated as a credit card customer
   */
  isUsernameAuthenticatedAsCreditCardCustomer(username: string): boolean {
    if (!username) {
      return false;
    }

    return this.isUsernameAuthenticated(username) && this.isCreditCardCustomer();
  }

  /**
   * @description Check if username is authenticated as a subscription customer or a supplier
   * @returns {boolean} True if username is authenticated as a subscription customer or a supplier
   */
  isUsernameAuthenticatedAsSubscriptionCustomerOrSupplier(username: string): boolean {
    if (!username) {
      return false;
    }

    return this.isUsernameAuthenticated(username) && (this.isSubscriptionCustomer() || this.isSupplier());
  }

  /**
   * Returns whether there is a token and the token is valid
   * @returns {boolean}
   */
  isValidToken(): boolean {
    return tokenService.isValidToken();
  }

  /**
   * Returns whether the single invoice option should be hidden
   * @returns {boolean}
   */
  shouldHideSingleInvoice(): Promise<boolean> | boolean {
    return (
      !this.isAuthenticated() ||
      this.isAnonymousCustomer() ||
      this.isCreditCardCustomer() ||
      tokenService.shouldHideSingleInvoice()
    );
  }
}

const createInitialStoreState = (): CoreState => ({
  logger: null,
  settings: getSettings(),
  user: null
});

const coreStoreInstance = new CoreStore(stateReducer, eventCollector, createInitialStoreState());
export default coreStoreInstance;
